import { customElement } from 'lit/decorators.js';
import { consume } from '@lit/context';
import {
  ltnToastServiceContext,
  nsSettingsServiceContext,
  ltnAuthServiceContext,
  nsDirectoryServiceContext,
} from '../ns-services/contexts.js';
import { LitElement } from 'lit';
import { ButtressDbService, buttressDbServiceContext } from '@buttress/crag';

import { LtnToastService } from '../ltn-toast/ltn-toast-service.js';
import { LtnAuthService } from '../ltn-auth/ltn-auth-service.js';

import { NSSettingsService } from '../ns-settings/ns-settings-service.js';
import { NSDirectoryService } from '../ns-services/ns-directory-service.js';

import Workflow from '../models/workflow.js';
import WorkflowSchema from '../models/workflow-schema.js';

import { JobInputValues } from '../models/job.js';
import { JobOutcomeInputValues } from '../models/job-schema.js';
import type { StartableWorkflow } from '../models/company-workflow.js';

import { callLambda, codedResultError, getActionFromLambdaResults, lambdaErrorCode } from '../helpers/lambda.js';
import { shownAsWaitingOverlay } from '../helpers/job-ledger-gate.js';
import { requiredInputKeys, resolveWorkflowEntry, withLaunchContext } from './workflow-entry.js';
import { pushRecentWorkflow } from './recent-workflows.js';

import Jobs from './jobs/index.js';
import NSJobComponent from './jobs/job.js';
import { createLogger } from '../ns-services/logger.js';
import { viewPath } from '../helpers/route.js';
import { ButtressSubscriptions } from '../helpers/buttress-subscriptions.js';

const logger = createLogger('ns-workflow-service');

/**
 * This service is used to manage the loading / inflating of workflows and
 * they're dependencies.
 */
@customElement('ns-workflow-service')
export class NSWorkflowService extends LitElement {
  private __workflowSchema?: WorkflowSchema[];

  private __resolves: any[] = [];

  @consume({ context: nsSettingsServiceContext })
  private __settings?: NSSettingsService;

  @consume({ context: buttressDbServiceContext })
  private __db?: ButtressDbService;

  @consume({ context: ltnAuthServiceContext })
  private __auth?: LtnAuthService;

  @consume({ context: ltnToastServiceContext })
  private __toast?: LtnToastService;

  @consume({ context: nsDirectoryServiceContext })
  private __directoryService?: NSDirectoryService;

  private readonly __subscriptions = new ButtressSubscriptions(this, () => this.__db);

  async connectedCallback() {
    super.connectedCallback();
    await this.updateComplete;

    if (!this.__settings) throw new Error('Unable to find settings service');

    if (!this.__db) throw new Error('Unable to find db service');

    if (!this.__auth) throw new Error('Unable to find auth service');

    if (!this.__directoryService) throw new Error('Unable to find NSWorkflowService');

    if (!this.__toast) throw new Error('Unable to find toast service');

    // Could maybe do with a listener on ButtressDbService for when the user is connected.
    this.__subscriptions.subscribe('workflowSchema.*', () => this.__workflowChanged());
  }

  async __workflowChanged() {
    // Debounce this maybe?
    const query = await this.__db?.query(
      'workflowSchema',
      {},
      {
        limit: 0,
      },
    );
    if (!query) throw new Error('Unable to query workflows');

    this.__workflowSchema = query.results as unknown as WorkflowSchema[];

    // If we have some resolves... resolve them
    if (this.__resolves.length > 0) {
      this.__resolves.forEach((resolve: Function) => resolve());
      this.__resolves = [];
    }
  }

  get workflowSchema() {
    return this.__workflowSchema;
  }

  // The versions people launch: each staff workflow's current one, and the company's published ones
  get currentWorkflowSchemas() {
    return this.__workflowSchema?.filter((workflow) =>
      workflow.kind === 'COMPANY' ? workflow.status === 'PUBLISHED' : workflow.current,
    );
  }

  async getWorkflowSchema(): Promise<WorkflowSchema[]> {
    if (this.__workflowSchema !== undefined) {
      return this.__workflowSchema;
    }

    return await new Promise((resolve: Function) => this.__resolves.push(resolve));
  }

  // With no version, the one people launch
  async findWorkflowSchema(name: string, version?: string) {
    await this.getWorkflowSchema();
    return this.workflowSchema?.find(
      (workflow) => workflow.name === name && (version ? workflow.version === version : workflow.current),
    );
  }

  // ? Would maybe expect the workflow to be returned from this rather than the results.
  async createWorkflowFromSchema(
    workflowSchema: WorkflowSchema,
    entryKey: string,
    inputValues: JobInputValues,
    // workflow/create refuses record fields and metadata in the body: entries take them as inputs
    workflow: Partial<Pick<Workflow, 'name' | 'parentId'>> = {},
  ) {
    if (!this.__auth) throw new Error('Missing auth service');

    resolveWorkflowEntry(workflowSchema, entryKey, inputValues);

    return this.__postWorkflowCreate(entryKey, inputValues, {
      ...workflow,
      workflowName: workflowSchema.name,
      workflowVersion: workflowSchema.version,
    });
  }

  private async __postWorkflowCreate(entry: string, inputs: JobInputValues, workflow: Record<string, unknown>) {
    if (!this.__auth) throw new Error('Missing auth service');
    return callLambda('workflow/create', 'POST', this.__auth.token, JSON.stringify({ entry, inputs, workflow }));
  }

  async loadJobComponent(jobName: string, _version: string): Promise<typeof NSJobComponent> {
    const comp = Jobs[jobName];
    if (!comp) throw new Error(`Unable to find job component: ${jobName}`);
    return comp;
  }

  async jobOutcome(jobId: string, outcomeKey: string, output: JobOutcomeInputValues) {
    try {
      await this.__jobComplete(jobId, outcomeKey, output);

      this.__toast?.queueToast({
        label: 'Job Completed',
        timeout: 4000,
      });
    } catch (err) {
      // A refusal the card shows as "Waiting for …" (#25 phase 5) says enough there; a toast beside it would repeat it as
      // an error
      if (!shownAsWaitingOverlay(lambdaErrorCode(err), err instanceof Error ? err.message : null)) {
        this.__toast?.queueToast({
          label: `${err}`,
          timeout: 4000,
        });
      }

      throw err;
    }
  }

  async evaluateNode(workflowId: string, nodeIdx?: number) {
    if (!this.__db) throw new Error('Missing db service');
    if (!this.__auth) throw new Error('Missing auth service');
    if (!this.__directoryService) throw new Error('Missing dir service');

    return await callLambda(
      'workflow/condition/evaluation',
      'POST',
      this.__auth.token,
      JSON.stringify({
        companyId: this.__directoryService?.company?.id,
        workflowId,
        nodeIdx,
      }),
    );
  }

  private async __jobComplete(jobId: string, outcome: string, inputs?: JobOutcomeInputValues) {
    if (!this.__auth) throw new Error('Missing auth service');
    if (!this.__directoryService) throw new Error('Missing dir service');

    const data = await callLambda(
      'job/complete',
      'POST',
      this.__auth.token,
      JSON.stringify({
        jobId,
        outcome,
        inputs,
      }),
    );

    // A coded refusal (the public ledger checks, stage 9 and #25 phase 5) keeps its code for the card
    const coded = codedResultError(data.res);
    if (coded) throw coded;
    if (data.res.code !== 200) {
      throw new Error(`Lambda error whilst running job complete: ${data.res.message}`);
    }

    return true;
  }

  // Errors are toasted here or by launchWorkflow, so callers only need the outcome.
  // `open` goes straight to the run's first job, for a launcher whose first step is the caller's own card.
  async launchByName(
    name: string,
    inputs: JobInputValues,
    successLabel: string,
    { open = false }: { open?: boolean } = {},
  ): Promise<boolean> {
    const workflowSchema = await this.findWorkflowSchema(name);
    if (!workflowSchema) {
      logger.error(`Workflow schema not found for ${name}`);
      this.__toast?.queueToast({ label: 'Unable to start this workflow', timeout: 4000 });
      return false;
    }

    let jobId: string | null;
    try {
      ({ jobId } = await this.__launch(workflowSchema, inputs));
    } catch {
      return false;
    }

    const jobPath = jobId ? `/feed/job/${jobId}` : '/feed';
    if (open && jobId) {
      viewPath(jobPath);
      return true;
    }

    this.__toast?.queueToast({
      label: successLabel,
      actionLabel: 'View',
      action: () => viewPath(jobPath),
      timeout: 6000,
    });
    return true;
  }

  async launchWorkflow(workflowSchema: WorkflowSchema, inputs?: JobInputValues): Promise<string> {
    return (await this.__launch(workflowSchema, inputs)).workflowId;
  }

  private async __launch(
    workflowSchema: WorkflowSchema,
    inputs?: JobInputValues,
  ): Promise<{ workflowId: string; jobId: string | null }> {
    try {
      if (!this.__db) throw new Error('Unable to find db service');
      if (!this.__directoryService) throw new Error('No directory service available');

      const me = this.__directoryService.me;
      if (!me) throw new Error('Unable to me from directory service');

      const company = this.__directoryService.company;
      if (!company) throw new Error('Unable to find company for the user');

      // Create a workflow container in the system.
      if (!workflowSchema.entry) {
        logger.info('No entry point for workflow', workflowSchema);
      }

      const { launcher } = workflowSchema.entry;
      if (!launcher) throw new Error(`No launcher entry point for workflow: ${workflowSchema.name}`);

      const launchInputs = withLaunchContext(launcher.inputs, inputs ?? {}, company.id, me.id);
      const missingInputKeys = requiredInputKeys(launcher.inputs).filter((key) => !launchInputs[key]);
      if (missingInputKeys.length > 0) {
        throw new Error(
          `Unable to launch workflow "${workflowSchema.name}": missing required input(s): ${missingInputKeys.join(', ')}`,
        );
      }

      logger.info('Creating workflow from schema', workflowSchema, launchInputs);

      const results = await this.createWorkflowFromSchema(workflowSchema, 'launcher', launchInputs);

      // Make sure the lambda returned that the workflow was created, and grab its id.
      const action = await getActionFromLambdaResults(results, 'workflow_created');
      if (typeof action === 'string') throw new Error('Unexpected action type');

      this.__recordRecentWorkflow(workflowSchema.name);

      const messages = Array.isArray(results.res.message) ? results.res.message : [];
      const created = messages.find((a) => typeof a !== 'string' && a.action === 'job_created');
      return { workflowId: action.id, jobId: created && typeof created !== 'string' ? created.id : null };
    } catch (err) {
      this.__toast?.queueToast({
        label: `${err}`,
        timeout: 4000,
      });

      throw err;
    }
  }

  // A connected company's workflow, whose schema this company can't read; returns the starter's own first job, if it gave them one
  async startConnectedWorkflow(startable: StartableWorkflow, relationshipId?: string): Promise<string | null> {
    try {
      const results = await this.__postWorkflowCreate(
        startable.entry,
        relationshipId ? { [startable.relationshipInput]: relationshipId } : {},
        { workflowName: startable.workflowName, workflowVersion: startable.workflowVersion },
      );
      const { code, message } = results.res;
      if (code !== 200) throw new Error(typeof message === 'string' ? message : 'Unable to start the workflow.');
      const created = (Array.isArray(message) ? message : []).find(
        (action) => typeof action !== 'string' && action.action === 'job_created',
      );
      if (!created || typeof created === 'string') return null;
      // The run's first step may be the owner's, which the starter can't open
      const job = await this.__db
        ?.getById<{ id: string; assignedTo?: string | null }>('job', created.id)
        .catch(() => null);
      return job?.assignedTo && job.assignedTo === this.__directoryService?.me?.id ? created.id : null;
    } catch (err) {
      this.__toast?.queueToast({ label: err instanceof Error ? err.message : `${err}`, timeout: 4000 });
      throw err;
    }
  }

  // Namespaced by user so switching accounts on the same browser doesn't leak recent workflows
  // between them - mirrors the pattern used for the public ledger device ID in ns-app.ts.
  private __recentWorkflowsKey(): string {
    return `${this.__directoryService?.me?.userId ?? 'anonymous'}_NS_RECENT_WORKFLOWS`;
  }

  getRecentWorkflowNames(): string[] {
    const raw = localStorage.getItem(this.__recentWorkflowsKey());
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private __recordRecentWorkflow(name: string) {
    const updated = pushRecentWorkflow(this.getRecentWorkflowNames(), name);
    localStorage.setItem(this.__recentWorkflowsKey(), JSON.stringify(updated));
  }
}
